import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import type { PerformFailure } from "./review-store";
import { performReview } from "./review-store";
import { REVIEW_FIELDS } from "./review-flow";
import type { BrainingEstimate } from "./types";

/**
 * Переход согласования с экрана.
 *
 * Сюда приходит действие, а не новая смета: «отправить», «согласовать»,
 * «нужны правки». Что из этого выйдет, решает `review-flow.ts` по статусу в
 * базе и роли из сессии — клиенту в этом вопросе не верим ни в чём, кроме
 * отпечатка содержания, которое он видел.
 *
 * Наружу возвращаются только поля согласования и отметка записи, а не смета
 * целиком: у человека на экране могут быть правки, сделанные за время
 * запроса, и подменять ими всю смету значило бы их стереть.
 */

const Payload = z.object({
  id: z.string().uuid(),
  action: z.enum(["submit", "withdraw", "approve", "request_changes", "reopen"]),
  comment: z.string().max(2000).optional(),
  /** отпечаток содержания, которое человек видел, — см. `contentFingerprint` */
  fingerprint: z.string().max(32).optional(),
});

export type ReviewPatch = Pick<BrainingEstimate, (typeof REVIEW_FIELDS)[number] | "savedAt">;

export type ReviewResponse =
  | { ok: true; patch: ReviewPatch }
  | { ok: false; code: PerformFailure; error: string };

const _reviewEstimate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => Payload.parse(d))
  .handler(async ({ data, context }): Promise<ReviewResponse> => {
    const res = await performReview(
      data.id,
      data.action,
      { email: context.auth.email, role: context.auth.role },
      { comment: data.comment, fingerprint: data.fingerprint },
    );
    if (!res.ok) return res;

    const patch: Record<string, unknown> = { savedAt: res.estimate.savedAt };
    // Снятое поле уезжает как null, а не пропадает: иначе на экране осталась
    // бы прежняя дата, и статус не сменился бы до перезагрузки.
    for (const field of REVIEW_FIELDS) patch[field] = res.estimate[field] ?? null;

    return sanitizeForSeroval("reviewEstimate", { ok: true as const, patch }) as ReviewResponse;
  });

export const reviewEstimate = _reviewEstimate;
